import { renderToString } from "react-dom/server";
import styled from "styled-components";

import { Info24px } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TextAnchorCell, tooltipContainerConfig } from "@similarweb/pro-logic-components";
import { colorsPalettes, type StrictColor } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChangeValue, ChartTooltip, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter, percentageSignFilter, spendFilter } from "app/filters/ngFilters";

import { EDataType } from "../../advertisers/overview/components/types";
import { ColorCircle } from "../../common/StyledComponents";
import { NA_VALUE } from "../../common/utils/utils";

import { CHANNEL_CONFIG, CHANNEL_IDS, CHART_DIMENSIONS } from "./constants";
import type { IChartData } from "./types";

const InfoIcon = styled(Info24px).attrs({
    size: 24,
    color: colorsPalettes.carbon[200],
})`
    margin-top: 2px;
`;

export const formatMetricValue = (value: number, metricType: EDataType): string => {
    if (metricType === EDataType.SPEND) {
        return spendFilter(value, abbrNumberFilter());
    }
    return abbrNumberFilter()(value) as string;
};

const getColumnLabels = (metricType: EDataType) => ({
    metricLabel: translate(`ad.intelligence.overview.performance.over.time.widget.${metricType}`),
    shareLabel: translate("ad.intelligence.advertiser.activity.overview.tracked.activity.estimates.table.share"),
    changeLabel: translate("ad.intelligence.advertiser.activity.overview.tracked.activity.estimates.table.change"),
});

export const getTooltipColumnDefinition = (metricType: EDataType): Array<ColumnDefinition<IChartData>> => {
    const { metricLabel, shareLabel, changeLabel } = getColumnLabels(metricType);

    return [
        {
            key: "0",
            title: "Channel",
            selector: "name" as const,
            align: "left" as const,
            size: "80px",
            render: (row: IChartData) => (
                <ChartTooltip.LabelCell
                    label={row.name}
                    backgroundColor={row.color}
                />
            ),
        },
        {
            key: "1",
            title: metricLabel,
            selector: "value" as const,
            align: "right" as const,
            size: "80px",
            render: (row: IChartData) => {
                const formattedValue = row.value > 0 ? formatMetricValue(row.value, metricType) : NA_VALUE;
                return <ChartTooltip.ValueCell value={formattedValue} />;
            },
        },
        {
            key: "2",
            title: shareLabel,
            selector: "share" as const,
            align: "right" as const,
            size: "60px",
            render: (row: IChartData) => (
                <ChartTooltip.ValueCell value={percentageSignFilter()(row.share, 0, NA_VALUE) as string} />
            ),
        },
        {
            key: "3",
            title: changeLabel,
            selector: "change" as const,
            align: "right" as const,
            size: "80px",
            render: (row: IChartData) => {
                if (row.change === 0) {
                    return <ChartTooltip.ValueCell value={NA_VALUE} />;
                }
                return (
                    <ChartTooltip.ChangeCell
                        value={minimumValueChangeFilter()(row.change, 0)}
                        isDecrease={row.change < 0}
                        unsigned={row.change === 0}
                    />
                );
            },
        },
    ];
};

export const getTableColumnDefinition = (
    metricType: EDataType,
    onChannelClick: (channelId: string) => void,
): ColumnsType<IChartData> => {
    const { metricLabel, shareLabel, changeLabel } = getColumnLabels(metricType);

    return [
        {
            dataIndex: "name",
            title: translate("ad.intelligence.advertiser.activity.overview.tracked.activity.estimates.table.channel"),
            resize: false,
            render: (_value, row) => {
                const channelConfig = CHANNEL_CONFIG[row.id];
                const channelName = translate(`ad.intelligence.advertiser.activity.overview.channel.${row.id}`);

                return (
                    <Flex
                        alignItems="center"
                        gap="12px"
                    >
                        <ColorCircle color={row.color} />
                        <Flex
                            alignItems="center"
                            gap="8px"
                        >
                            <TextAnchorCell onClick={() => onChannelClick(row.id)}>{channelName}</TextAnchorCell>
                            {channelConfig?.hasNoMetricData ? (
                                <Tooltip
                                    content={translate(
                                        "ad.intelligence.advertiser.activity.overview.channel.coming.soon.tooltip",
                                    )}
                                >
                                    <span>
                                        <InfoIcon />
                                    </span>
                                </Tooltip>
                            ) : null}
                        </Flex>
                    </Flex>
                );
            },
            width: 140,
        },
        {
            dataIndex: "value",
            title: metricLabel,
            width: 120,
            align: "right",
            resize: false,
            render: (value: number, row) => {
                const channelConfig = CHANNEL_CONFIG[row.id];
                if (channelConfig?.hasNoMetricData) {
                    return null;
                }

                const formattedValue = value > 0 ? formatMetricValue(value, metricType) : NA_VALUE;
                const showInfoIcon = row.id === CHANNEL_IDS.search && metricType === EDataType.IMPRESSIONS;

                if (showInfoIcon) {
                    return (
                        <Flex
                            alignItems="center"
                            justifyContent="flex-end"
                            gap="4px"
                        >
                            <Tooltip
                                content={translate(
                                    "ad.intelligence.advertiser.activity.overview.search.impressions.tooltip",
                                )}
                            >
                                <span>
                                    <InfoIcon />
                                </span>
                            </Tooltip>
                            <span>{formattedValue}</span>
                        </Flex>
                    );
                }

                return formattedValue;
            },
        },
        {
            dataIndex: "share",
            title: shareLabel,
            width: 80,
            align: "right",
            resize: false,
            render: (value: number, row) => {
                const channelConfig = CHANNEL_CONFIG[row.id];
                if (channelConfig?.hasNoMetricData) {
                    return null;
                }
                return value > 0 ? (percentageSignFilter()(value, 0) as string) : NA_VALUE;
            },
        },
        {
            dataIndex: "change",
            title: changeLabel,
            width: 96,
            align: "right",
            resize: false,
            render: (value: number, row) => {
                const channelConfig = CHANNEL_CONFIG[row.id];
                if (channelConfig?.hasNoMetricData) {
                    return null;
                }
                if (value === 0) {
                    return NA_VALUE;
                }
                return (
                    <ChangeValue
                        value={minimumValueChangeFilter()(value, 0)}
                        isDecrease={value < 0}
                    />
                );
            },
        },
    ];
};

export const getTrackedActivityDonutChartConfig = (metricType: EDataType) => {
    const columnDefinition = getTooltipColumnDefinition(metricType);

    return {
        chart: {
            height: CHART_DIMENSIONS.height,
            width: CHART_DIMENSIONS.width,
            spacing: [0, 0, 0, 0] as [number, number, number, number],
        },
        tooltip: {
            enabled: true,
            followPointer: true,
            headerFormat: "",
            ...tooltipContainerConfig().tooltip,
            useHTML: true,
            pointFormatter(this: Highcharts.Point) {
                const options = this.options as IChartData;

                const dataRow: IChartData = {
                    name: translate(`ad.intelligence.advertiser.activity.overview.channel.${options.id}`),
                    id: options.id,
                    color: this.color as StrictColor,
                    y: this.y ?? 0,
                    share: options.share ?? 0,
                    value: options.value ?? 0,
                    change: options.change ?? 0,
                    visible: true,
                    metricType: options.metricType,
                };

                return renderToString(
                    <ChartTooltip
                        width={360}
                        title={null}
                        columnDefinition={columnDefinition}
                        data={[dataRow]}
                    />,
                );
            },
        },
    };
};
